import { WalletApiRequestSignatureInput } from "@privy-io/node";
import { useAuthorizationSignature } from "@privy-io/react-auth";
import { SendTransactionParameters, SignMessageParameters, WriteContractParameters } from "@wagmi/core";
import { useWeb3 } from "logic/components";
import { useCallback, useMemo } from "react";
import { Address, encodeFunctionData, isAddressEqual } from "viem";

import { getRequestExpiry } from "./getRequestExpiry";
import { sendPrivyBatchTransaction } from "./sendPrivyBatchTransaction";
import { sendPrivyTransaction } from "./sendPrivyTransaction";
import { signPrivy7702Authorization } from "./SignPrivy7702Authorization";
import { signPrivyMessage } from "./signPrivyMessage";
import { usePrivyUserMetadata } from "./usePrivyUserMetadata";

export function usePrivySignerTxs() {
  const userMetadata = usePrivyUserMetadata();
  const { address } = useWeb3();
  const wallet = useMemo(
    () => address && userMetadata?.linkedWallets.find((w) => isAddressEqual(w.address, address)),
    [userMetadata, address],
  );
  const { generateAuthorizationSignature } = useAuthorizationSignature();

  const signMessage = useCallback(
    async (params: SignMessageParameters) => {
      if (!wallet) throw new Error("No wallet found");

      const message = params.message.toString();
      const requestExpiry = getRequestExpiry();

      const payload: WalletApiRequestSignatureInput = {
        version: 1,
        method: "POST",
        url: `https://api.privy.io/v1/wallets/${wallet.wallet_id}/rpc`,
        headers: {
          "privy-app-id": process.env.NEXT_PUBLIC_PRIVY_APP_ID ?? "",
          "privy-request-expiry": String(requestExpiry),
        },
        body: {
          method: "personal_sign",
          chain_type: "ethereum",
          params: {
            message: message,
            encoding: "utf-8",
          },
        },
      };

      const { signature: authSignature } = await generateAuthorizationSignature(payload);

      const signature = await signPrivyMessage({
        message: message,
        walletId: wallet.wallet_id,
        signature: authSignature,
      });

      return signature;
    },
    [wallet, generateAuthorizationSignature],
  );

  const sendTransaction = useCallback(
    async (params: WriteContractParameters | SendTransactionParameters) => {
      // if (!isAdditionalSigner) throw new Error("Not an additional signer"); // TODO: Uncomment this

      if (!wallet) throw new Error("No wallet found");
      // generate the authorization signature using the payload
      const to = "address" in params ? params.address : params.to;
      const txData =
        "abi" in params
          ? encodeFunctionData({
              abi: params.abi,
              functionName: params.functionName,
              args: params.args,
            })
          : params.data;

      const requestExpiry = getRequestExpiry();

      const payload: WalletApiRequestSignatureInput = {
        version: 1,
        method: "POST",
        url: `https://api.privy.io/v1/wallets/${wallet.wallet_id}/rpc`,
        headers: {
          "privy-app-id": process.env.NEXT_PUBLIC_PRIVY_APP_ID ?? "",
          "privy-request-expiry": String(requestExpiry),
        },
        body: {
          method: "eth_sendTransaction",
          caip2: `eip155:${params.chainId}`,
          chain_type: "ethereum",
          sponsor: true,
          params: {
            transaction: {
              to,
              data: txData,
              chain_id: `0x${params.chainId?.toString(16) ?? "0"}`,
              from: address,
              value: `0x${params.value?.toString(16) ?? "0"}`,
            },
          },
        },
      };
      const authSignature = await generateAuthorizationSignature(payload);

      const data = await sendPrivyTransaction({
        walletId: wallet.wallet_id,
        txParams: params,
        signature: authSignature.signature,
      });

      return data.hash;
    },
    [generateAuthorizationSignature, address, wallet],
  );

  const sign7702Authorization = useCallback(
    async (params: { contractAddress: Address; chainId: number }) => {
      if (!wallet) throw new Error("No wallet found");

      const requestExpiry = getRequestExpiry();

      const payload: WalletApiRequestSignatureInput = {
        version: 1,
        method: "POST",
        url: `https://api.privy.io/v1/wallets/${wallet.wallet_id}/rpc`,
        headers: {
          "privy-app-id": process.env.NEXT_PUBLIC_PRIVY_APP_ID ?? "",
          "privy-request-expiry": String(requestExpiry),
        },
        body: {
          method: "eth_sign7702Authorization",
          chain_type: "ethereum",
          params: {
            chain_id: `0x${params.chainId?.toString(16) ?? "0"}`,
            contract: params.contractAddress,
          },
        },
      };
      const authSignature = await generateAuthorizationSignature(payload);

      return await signPrivy7702Authorization({
        contractAddress: params.contractAddress,
        chainId: params.chainId,
        walletId: wallet.wallet_id,
        signature: authSignature.signature,
      });
    },
    [generateAuthorizationSignature, wallet],
  );

  const sendBatchTransaction = useCallback(
    async (paramsArray: WriteContractParameters[]) => {
      if (!wallet) throw new Error("No wallet found");
      if (paramsArray.length === 0) throw new Error("sendBatchTransaction requires at least one call");

      const chainIds = new Set(paramsArray.map((p) => p.chainId));
      if (chainIds.size > 1) {
        throw new Error(
          `All calls in a batch must target the same chain, but found chainIds: ${[...chainIds].join(", ")}`,
        );
      }

      const calls = paramsArray.map((params) => {
        const to = params.address;
        const data = encodeFunctionData({
          abi: params.abi,
          functionName: params.functionName,
          args: params.args,
        });

        return {
          to: to as string,
          data: data as string,
          value: `0x${params.value?.toString(16) ?? "0"}`,
        };
      });

      const chainId = paramsArray[0]!.chainId!;
      const requestExpiry = getRequestExpiry();

      const payload: WalletApiRequestSignatureInput = {
        version: 1,
        method: "POST",
        url: `https://api.privy.io/v1/wallets/${wallet.wallet_id}/rpc`,
        headers: {
          "privy-app-id": process.env.NEXT_PUBLIC_PRIVY_APP_ID ?? "",
          "privy-request-expiry": String(requestExpiry),
        },
        body: {
          method: "wallet_sendCalls",
          caip2: `eip155:${chainId}`,
          chain_type: "ethereum",
          sponsor: true,
          params: { calls },
        },
      };

      const authSignature = await generateAuthorizationSignature(payload);

      return sendPrivyBatchTransaction({
        calls,
        chainId: Number(chainId),
        walletId: wallet.wallet_id,
        signature: authSignature.signature,
      });
    },
    [generateAuthorizationSignature, wallet],
  );

  return { sendTransaction, sendBatchTransaction, signMessage, sign7702Authorization };
}
